/**
 * API Utilities
 * 
 * Centralized API calls with error handling.
 */

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Generic API request handler with error handling.
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_URL}${endpoint}`;
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...options
  };
  
  // Convert body to JSON if it's an object
  if (config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body);
  }
  
  const response = await fetch(url, config);
  
  // Parse JSON response
  const data = await response.json();
  
  // Throw error for non-2xx responses
  if (!response.ok) {
    const error = new Error(data.error || 'An error occurred');
    error.status = response.status;
    error.data = data;
    throw error;
  }
  
  return data;
}

// ============================================================================
// Quiz API
// ============================================================================

/**
 * Create a new quiz.
 * @param {Object} quizData - { title, description }
 * @returns {Promise<Object>} Created quiz with id
 */
export async function createQuiz(quizData) {
  return apiRequest('/quizzes', {
    method: 'POST',
    body: quizData
  });
}

/**
 * Get a quiz with all its questions (for management).
 * @param {string} quizId - The quiz ID
 * @returns {Promise<Object>} Quiz with questions array
 */
export async function getQuiz(quizId) {
  return apiRequest(`/quizzes/${quizId}`);
}

/**
 * Update quiz title and description.
 * @param {string} quizId - The quiz ID
 * @param {Object} quizData - { title, description }
 * @returns {Promise<Object>} Success message
 */
export async function updateQuiz(quizId, quizData) {
  return apiRequest(`/quizzes/${quizId}`, {
    method: 'PUT',
    body: quizData
  });
}

/**
 * Delete a quiz and all its questions.
 * @param {string} quizId - The quiz ID
 * @returns {Promise<Object>} Success message
 */
export async function deleteQuiz(quizId) {
  return apiRequest(`/quizzes/${quizId}`, {
    method: 'DELETE'
  });
}

/**
 * List all quizzes with question counts.
 * @returns {Promise<Array>} Array of quizzes
 */
export async function listQuizzes() {
  return apiRequest('/quizzes');
}

// ============================================================================
// Question API
// ============================================================================

/**
 * Add a question to a quiz.
 * @param {string} quizId - The quiz ID
 * @param {Object} questionData - Question data
 * @returns {Promise<Object>} Created question with id
 */
export async function addQuestion(quizId, questionData) {
  return apiRequest(`/quizzes/${quizId}/questions`, {
    method: 'POST',
    body: questionData
  });
}

/**
 * Update a question.
 * @param {number} questionId - The question ID
 * @param {Object} questionData - Updated question data
 * @returns {Promise<Object>} Success message
 */
export async function updateQuestion(questionId, questionData) {
  return apiRequest(`/questions/${questionId}`, {
    method: 'PUT',
    body: questionData
  });
}

/**
 * Delete a question.
 * @param {number} questionId - The question ID
 * @returns {Promise<Object>} Success message
 */
export async function deleteQuestion(questionId) {
  return apiRequest(`/questions/${questionId}`, {
    method: 'DELETE'
  });
}

/**
 * Reorder questions in a quiz.
 * @param {string} quizId - The quiz ID
 * @param {Array<number>} questionIds - Array of question IDs in desired order
 * @returns {Promise<Object>} Success message
 */
export async function reorderQuestions(quizId, questionIds) {
  return apiRequest(`/quizzes/${quizId}/questions/reorder`, {
    method: 'PUT',
    body: { question_ids: questionIds }
  });
}

// ============================================================================
// Play API
// ============================================================================

/**
 * Get a quiz for playing (without correct answers).
 * @param {string} quizId - The quiz ID
 * @returns {Promise<Object>} Quiz with questions (no correct_answer field)
 */
export async function getQuizForPlay(quizId) {
  return apiRequest(`/play/${quizId}`);
}

/**
 * Check answers for a completed quiz.
 * @param {string} quizId - The quiz ID
 * @param {Object} answers - Map of questionId to answer letter
 * @returns {Promise<Object>} Score and results
 */
export async function checkAnswers(quizId, answers) {
  return apiRequest(`/play/${quizId}/check`, {
    method: 'POST',
    body: { answers }
  });
}
